import React, { useMemo, useRef, useState, useEffect, useCallback } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './BookingPage.css';
import PageHero from './PageHero';
import { Camp, Booking } from '../types';
import { camps } from '../data/camps';
import { isDateBlocked } from '../data/availability';
import { getAllBlockedDatesForCamp, saveBookingRequest } from '../utils/bookingStorage';
import { formatLocalYYYYMMDD } from '../utils/dateUtils';
import { sendEnquiryEmail } from '../utils/emailService';
import { MIN_BOOKING_NIGHTS } from '../constants/booking';

function countNights(checkIn: Date, checkOut: Date): number {
  return Math.round((checkOut.getTime() - checkIn.getTime()) / (1000 * 60 * 60 * 24));
}

/** Earliest allowed check-out for a given check-in (enforces the minimum stay). */
function minCheckOut(checkIn: Date): Date {
  const d = new Date(checkIn);
  d.setDate(d.getDate() + MIN_BOOKING_NIGHTS);
  return d;
}

interface BookingPageProps {
  preSelectedCampId?: string;
}

const MOBILE_COLLAGE_MQ = '(max-width: 768px)';

function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia(query).matches : false
  );
  useEffect(() => {
    const mq = window.matchMedia(query);
    const handler = () => setMatches(mq.matches);
    mq.addEventListener('change', handler);
    setMatches(mq.matches);
    return () => mq.removeEventListener('change', handler);
  }, [query]);
  return matches;
}

const BookingPage: React.FC<BookingPageProps> = ({ preSelectedCampId }) => {
  const [selectedCamp, setSelectedCamp] = useState<Camp | null>(
    preSelectedCampId ? camps.find(c => c.id === preSelectedCampId) || null : null
  );
  const bookingDetailsRef = useRef<HTMLDivElement | null>(null);
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const [galleryIndex, setGalleryIndex] = useState(0);
  const thumbsRef = useRef<HTMLDivElement | null>(null);
  const galleryTouchStartX = useRef<number | null>(null);
  const isMobileCollage = useMediaQuery(MOBILE_COLLAGE_MQ);

  const assetImages = useMemo<string[]>(() => {
    return selectedCamp?.images?.length ? selectedCamp.images : [];
  }, [selectedCamp]);

  const collageImages = useMemo<string[]>(() => {
    const unique = Array.from(new Set(assetImages));
    // 1 hero + 2 stacked right + 4 bottom thumbs = 7 visible images
    return unique.slice(0, 7);
  }, [assetImages]);

  const scrollToBookingDetails = useCallback(() => {
    const el = bookingDetailsRef.current;
    if (!el) return;
    const top = el.getBoundingClientRect().top + window.scrollY - 90;
    window.scrollTo({ top, behavior: 'smooth' });
  }, []);

  const handleSelectCamp = useCallback((camp: Camp) => {
    setSelectedCamp(camp);
    // allow booking form to render before scrolling
    setTimeout(() => scrollToBookingDetails(), 50);
  }, [scrollToBookingDetails]);

  const openGalleryAt = useCallback((index: number) => {
    if (!assetImages.length) return;
    setGalleryIndex(Math.max(0, Math.min(index, assetImages.length - 1)));
    setIsGalleryOpen(true);
  }, [assetImages]);

  const closeGallery = useCallback(() => setIsGalleryOpen(false), []);
  const showPrev = useCallback(() => {
    setGalleryIndex((i) => (i - 1 + assetImages.length) % assetImages.length);
  }, [assetImages.length]);
  const showNext = useCallback(() => {
    setGalleryIndex((i) => (i + 1) % assetImages.length);
  }, [assetImages.length]);

  const onGalleryTouchStart = useCallback((e: React.TouchEvent) => {
    const t = e.target as HTMLElement;
    if (t.closest('.asset-gallery-nav') || t.closest('.asset-gallery-close')) {
      galleryTouchStartX.current = null;
      return;
    }
    galleryTouchStartX.current = e.targetTouches[0].clientX;
  }, []);

  const onGalleryTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      if (galleryTouchStartX.current == null || assetImages.length < 2) return;
      const dx = e.changedTouches[0].clientX - galleryTouchStartX.current;
      galleryTouchStartX.current = null;
      if (Math.abs(dx) < 48) return;
      if (dx > 0) showPrev();
      else showNext();
    },
    [assetImages.length, showNext, showPrev]
  );

  const onGalleryTouchCancel = useCallback(() => {
    galleryTouchStartX.current = null;
  }, []);

  const THUMB_WINDOW = 5;
  const visibleThumbs = useMemo(() => {
    const total = assetImages.length;
    const half = Math.floor(THUMB_WINDOW / 2); // 2

    // Always render 5 "slots" so the strip doesn't shift on the first/last images.
    // When we're near an edge, we render invisible placeholders for out-of-range slots.
    const slots = Array.from({ length: THUMB_WINDOW }, (_, i) => {
      const idx = galleryIndex + (i - half);
      if (idx < 0 || idx >= total) return { idx, src: null as string | null, placeholder: true };
      return { idx, src: assetImages[idx], placeholder: false };
    });

    // If fewer than 5 images, just show them (no placeholders needed).
    if (total <= THUMB_WINDOW) {
      return assetImages.map((src, idx) => ({ idx, src, placeholder: false }));
    }

    return slots;
  }, [assetImages, galleryIndex]);

  useEffect(() => {
    if (!isGalleryOpen) return;
    const el = thumbsRef.current;
    if (!el) return;
    const active = el.querySelector<HTMLButtonElement>('.asset-thumb.active');
    if (!active) return;
    active.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  }, [galleryIndex, isGalleryOpen, visibleThumbs.length]);

  useEffect(() => {
    if (preSelectedCampId) {
      const camp = camps.find(c => c.id === preSelectedCampId);
      if (camp) {
        setSelectedCamp(camp);
        setTimeout(() => scrollToBookingDetails(), 50);
      }
    }
  }, [preSelectedCampId, scrollToBookingDetails]);

  useEffect(() => {
    // if switching camps while gallery open, clamp index
    setGalleryIndex((i) => Math.max(0, Math.min(i, Math.max(0, assetImages.length - 1))));
    if (isGalleryOpen && assetImages.length === 0) setIsGalleryOpen(false);
  }, [assetImages.length, isGalleryOpen]);

  useEffect(() => {
    if (!isGalleryOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeGallery();
      if (e.key === 'ArrowLeft') showPrev();
      if (e.key === 'ArrowRight') showNext();
    };
    document.addEventListener('keydown', onKeyDown);
    // Prevent background scroll while modal is open
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [isGalleryOpen, closeGallery, showPrev, showNext]);

  // Load blocked dates when camp is selected
  useEffect(() => {
    if (selectedCamp) {
      loadBlockedDates(selectedCamp.id);
    } else {
      setBlockedDatesCache(new Set());
    }
  }, [selectedCamp]);

  const loadBlockedDates = async (campId: string) => {
    try {
      const blockedDates = await getAllBlockedDatesForCamp(campId);
      setBlockedDatesCache(new Set(blockedDates));
    } catch (error) {
      console.error('Error loading blocked dates:', error);
      setBlockedDatesCache(new Set());
    }
  };
  const [checkIn, setCheckIn] = useState<Date | null>(null);
  const [checkOut, setCheckOut] = useState<Date | null>(null);
  const [guests, setGuests] = useState<number>(2);
  const [guestName, setGuestName] = useState<string>('');
  const [guestEmail, setGuestEmail] = useState<string>('');
  const [guestPhone, setGuestPhone] = useState<string>('');
  const [specialRequests, setSpecialRequests] = useState<string>('');
  const [bookingConfirmed, setBookingConfirmed] = useState<boolean>(false);
  const [bookingEmailSent, setBookingEmailSent] = useState<boolean>(true);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<string>('');
  const [blockedDatesCache, setBlockedDatesCache] = useState<Set<string>>(new Set());

  const getMinDate = (): Date => {
    const today = new Date();
    today.setDate(today.getDate() + 1); // Minimum is tomorrow
    today.setHours(0, 0, 0, 0);
    return today;
  };

  // Check if a date is disabled (blocked or in the past)
  const isDateDisabled = (date: Date): boolean => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Disable dates before tomorrow
    if (date < today) {
      return true;
    }
    
    // Check hardcoded blocked dates
    if (isDateBlocked(date)) {
      return true;
    }
    
    // Check cached blocked dates from Supabase (local calendar day, not UTC)
    const dateStr = formatLocalYYYYMMDD(date);
    if (blockedDatesCache.has(dateStr)) {
      return true;
    }
    
    return false;
  };

  // Filter dates that are blocked or unavailable
  const filterDate = (date: Date): boolean => {
    return !isDateDisabled(date);
  };

  // Preferred dates are optional for an enquiry; if both are given, check order and minimum stay.
  const validateDates = (): string | null => {
    if (!checkIn || !checkOut) return null;

    if (checkOut <= checkIn) {
      return 'Your preferred check-out date should be after your check-in date.';
    }

    if (countNights(checkIn, checkOut) < MIN_BOOKING_NIGHTS) {
      return `Minimum stay is ${MIN_BOOKING_NIGHTS} nights. Please adjust your preferred dates.`;
    }

    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError('');
    setIsSubmitting(true);
    
    if (!selectedCamp || !guestName || !guestEmail || !guestPhone) {
      setSubmitError('Please fill in your name, email and phone so we can reply.');
      setIsSubmitting(false);
      return;
    }

    // Preferred dates are optional; just check ordering if both are provided.
    const dateError = validateDates();
    if (dateError) {
      setSubmitError(dateError);
      setIsSubmitting(false);
      return;
    }

    try {
      const result = await sendEnquiryEmail({
        campName: selectedCamp.name,
        guestName,
        guestEmail,
        guestPhone,
        guests,
        preferredCheckIn: checkIn ? checkIn.toLocaleDateString() : undefined,
        preferredCheckOut: checkOut ? checkOut.toLocaleDateString() : undefined,
        nights: checkIn && checkOut ? countNights(checkIn, checkOut) : undefined,
        message: specialRequests || undefined,
      });

      // If the guest gave concrete preferred dates, also log the enquiry so it
      // appears in the admin manage-bookings page. Failure here is non-fatal —
      // the email has already been sent.
      if (checkIn && checkOut) {
        const nights = countNights(checkIn, checkOut);
        const enquiry: Booking = {
          id: `enquiry-${Date.now()}`,
          campId: selectedCamp.id,
          checkIn,
          checkOut,
          guests,
          totalPrice: nights * selectedCamp.pricePerNight,
          guestName,
          guestEmail,
          guestPhone,
          specialRequests: specialRequests || undefined,
        };
        try {
          await saveBookingRequest(enquiry, selectedCamp.name);
        } catch (logError) {
          console.warn('Could not log enquiry to manage-bookings:', logError);
        }
      }

      if (result.success) {
        setBookingEmailSent(result.emailSent !== false);
        setBookingConfirmed(true);
        setIsSubmitting(false);
      } else {
        setSubmitError(result.error || 'Failed to send your enquiry. Please try again.');
        setIsSubmitting(false);
      }
    } catch (error) {
      console.error('Error submitting enquiry:', error);
      setSubmitError('An error occurred while sending your enquiry. Please try again.');
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setSelectedCamp(null);
    setCheckIn(null);
    setCheckOut(null);
    setGuests(2);
    setGuestName('');
    setGuestEmail('');
    setGuestPhone('');
    setSpecialRequests('');
    setBookingConfirmed(false);
    setBookingEmailSent(true);
  };

  if (bookingConfirmed) {
    return (
      <div className="booking-page">
        <div className="booking-container">
          <div className="booking-confirmation">
            <div className="confirmation-icon">✓</div>
            <h2>Enquiry Sent!</h2>
            <div className="confirmation-details">
              <p><strong>Camp:</strong> {selectedCamp?.name}</p>
              <p><strong>Preferred check-in:</strong> {checkIn ? checkIn.toLocaleDateString() : 'Flexible'}</p>
              <p><strong>Preferred check-out:</strong> {checkOut ? checkOut.toLocaleDateString() : 'Flexible'}</p>
              <p><strong>Guests:</strong> {guests}</p>
            </div>
            <p className="confirmation-message">
              {bookingEmailSent ? (
                <>
                  Thanks {guestName.split(' ')[0] || 'there'}! Your enquiry has been sent to
                  Southern Comfort Camps. We&apos;ll be in touch at {guestEmail} to confirm
                  availability, answer any questions, and arrange the next steps.
                </>
              ) : (
                <>
                  Your enquiry could not be sent automatically because email is not configured
                  in this deployment. Please email us directly at{' '}
                  <a href="mailto:info@southerncomfortcamps.com">info@southerncomfortcamps.com</a>
                  {' '}with your preferred dates, or ask the site admin to set the{' '}
                  <code>REACT_APP_EMAILJS_*</code> environment variables and redeploy.
                </>
              )}
            </p>
            {bookingEmailSent && (
              <p className="confirmation-note">
                We usually reply within 24-48 hours. Thank you for your interest in
                Southern Comfort Camps!
              </p>
            )}
            <div className="confirmation-actions">
              <button onClick={handleReset} className="btn-secondary">
                Send Another Enquiry
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="booking-page">
      <PageHero 
        title="Book Your Stay"
        subtitle="Experience the Abrolhos Islands with Us"
      />
      <div className="booking-container">

        <div className="booking-content">
        <section className="before-you-book">
          <div className="before-you-book-inner">
            <h2>About a stay with us</h2>
            <p className="before-you-book-tagline">These are remote island properties</p>
            
            <div className="before-you-book-items">
              <div className="before-you-book-item">
                <span className="item-header">Vessel-Access Only:</span>
                <span className="item-text">We are located 35nm offshore. Guests must provide their own transport and be self-sufficient in remote maritime travel.</span>
              </div>
              
              <div className="before-you-book-item">
                <span className="item-header">On-Island Caretakers:</span>
                <span className="item-text">We live on the island to assist with your stay, though we are not a serviced hotel and booking is subject to host availability. Booking is by application and will be approved by the Caretakers.</span>
              </div>
              
              <div className="before-you-book-item">
                <span className="item-header">Self-Catering:</span>
                <span className="item-text">No shops or services are available on the island. Please arrive with all necessary provisions.</span>
              </div>
              
              <div className="before-you-book-item">
                <span className="item-header">Minimum Stay:</span>
                <span className="item-text">
                  Bookings are for a minimum of {MIN_BOOKING_NIGHTS} nights.
                </span>
              </div>

              <div className="before-you-book-item">
                <span className="item-header">Weather Dependent:</span>
                <span className="item-text">All stays are subject to sea and weather conditions.</span>
              </div>
            </div>
          </div>
        </section>

          <div className="camps-selection">
            <h2>Select Your Camp</h2>
            <div className="camps-grid">
              {camps.map((camp) => (
                <div
                  key={camp.id}
                  className={`camp-card ${selectedCamp?.id === camp.id ? 'selected' : ''}`}
                  role="button"
                  tabIndex={0}
                  onClick={() => handleSelectCamp(camp)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') handleSelectCamp(camp);
                  }}
                >
                  <div className="camp-card-image">
                    {camp.images && camp.images.length > 0 ? (
                      <img src={camp.images[0]} alt={camp.name} />
                    ) : (
                      <div className="camp-card-image-placeholder">🏕️</div>
                    )}
                  </div>
                  <div className="camp-card-header">
                    <h3>{camp.name}</h3>
                    <span className="camp-price">${camp.pricePerNight}/night</span>
                  </div>
                  <p className="camp-location">{camp.location}</p>
                  <p className="camp-description">{camp.description}</p>
                  <div className="camp-details">
                    <span>👥 Up to {camp.maxGuests} guests</span>
                  </div>
                  <div className="camp-amenities">
                    <strong>Amenities:</strong>
                    <ul>
                      {camp.amenities.map((amenity, idx) => (
                        <li key={idx}>{amenity}</li>
                      ))}
                    </ul>
                  </div>
                  <button
                    type="button"
                    className="camp-card-cta"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSelectCamp(camp);
                    }}
                  >
                    {selectedCamp?.id === camp.id ? 'Continue to booking ↓' : 'View booking details ↓'}
                  </button>
                </div>
              ))}
            </div>
          </div>

          {selectedCamp && (
            <div ref={bookingDetailsRef}>
              <section className="booking-collage">
                <div className="booking-collage-header">
                  <h2>{selectedCamp.name} photos</h2>
                  <p>Tap to view all photos</p>
                </div>

                <div className="booking-collage-rect">
                  {collageImages.length ? (
                    <>
                      <div className="booking-collage-top">
                        <div className="booking-collage-top-main">
                          <button
                            type="button"
                            className="collage-tile collage-hero"
                            onClick={() => openGalleryAt(0)}
                            aria-label="Open photo gallery"
                          >
                            <img src={collageImages[0]} alt="" />
                          </button>
                        </div>

                        <div className="booking-collage-top-side">
                          {collageImages[1] && (
                            <button
                              type="button"
                              className="collage-tile collage-side collage-side-top"
                              onClick={() => openGalleryAt(1)}
                              aria-label="Open photo gallery"
                            >
                              <img src={collageImages[1]} alt="" />
                            </button>
                          )}

                          {collageImages[2] && (
                            <button
                              type="button"
                              className="collage-tile collage-side collage-side-bottom"
                              onClick={() => openGalleryAt(2)}
                              aria-label="Open photo gallery"
                            >
                              <img src={collageImages[2]} alt="" />
                            </button>
                          )}
                        </div>
                      </div>

                      <div className="booking-collage-bottom">
                        {(() => {
                          const bottomStart = isMobileCollage ? 2 : 3;
                          const bottomSlice = collageImages.slice(bottomStart, bottomStart + 4);
                          const overflowAt = isMobileCollage ? 6 : 7;
                          return bottomSlice.map((src, localIdx) => {
                            const idx = bottomStart + localIdx;
                            const isLastSlot = localIdx === bottomSlice.length - 1;
                            const hasMore =
                              isLastSlot && assetImages.length > overflowAt;
                            const moreCount = assetImages.length - overflowAt;
                            return (
                              <button
                                key={`${src}-${idx}`}
                                type="button"
                                className="collage-tile collage-thumb"
                                onClick={() => openGalleryAt(idx)}
                                aria-label="Open photo gallery"
                              >
                                <img src={src} alt="" />
                                {hasMore && moreCount > 0 && (
                                  <span className="collage-more">+{moreCount}</span>
                                )}
                              </button>
                            );
                          });
                        })()}
                      </div>
                    </>
                  ) : (
                    <div className="collage-empty">No photos found for this camp.</div>
                  )}
                </div>
              </section>

              <div className="booking-form-section">
                <div className="booking-camp-context" aria-live="polite">
                  {selectedCamp.images && selectedCamp.images.length > 0 && (
                    <div className="booking-camp-thumb">
                      <img src={selectedCamp.images[0]} alt="" />
                    </div>
                  )}
                  <div className="booking-camp-text">
                    <p className="booking-camp-label">You&apos;re booking</p>
                    <p className="booking-camp-title">{selectedCamp.name}</p>
                    <p className="booking-camp-meta">
                      {selectedCamp.location} · ${selectedCamp.pricePerNight}/night · Up to{' '}
                      {selectedCamp.maxGuests} guests
                    </p>
                  </div>
                </div>
                <h2 className="booking-details-heading">Enquire About Your Stay</h2>
                <p className="booking-enquiry-intro">
                  Let us know your preferred dates and a few details, and we&apos;ll get back to you to confirm availability, answer any questions and arrange the next steps. All dates are subject to availability and weather.
                </p>
              <form onSubmit={handleSubmit} className="booking-form">
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="checkIn">Preferred Check-in (optional)</label>
                    <DatePicker
                      id="checkIn"
                      selected={checkIn}
                      onChange={(date: Date | null) => {
                        setCheckIn(date);
                        if (date && checkOut && countNights(date, checkOut) < MIN_BOOKING_NIGHTS) {
                          setCheckOut(null);
                        }
                        setSubmitError('');
                      }}
                      filterDate={filterDate}
                      minDate={getMinDate()}
                      dateFormat="MMMM d, yyyy"
                      placeholderText="Select a preferred date"
                      className="date-picker-input"
                      isClearable
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="checkOut">Preferred Check-out (optional)</label>
                    <DatePicker
                      id="checkOut"
                      selected={checkOut}
                      onChange={(date: Date | null) => {
                        setCheckOut(date);
                        setSubmitError('');
                      }}
                      filterDate={filterDate}
                      minDate={checkIn ? minCheckOut(checkIn) : getMinDate()}
                      dateFormat="MMMM d, yyyy"
                      placeholderText="Select a preferred date"
                      className="date-picker-input"
                      isClearable
                      disabled={!checkIn}
                    />
                  </div>
                  {submitError && (
                    <div className="form-error">
                      <p>{submitError}</p>
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="guests">Number of Guests</label>
                  <select
                    id="guests"
                    value={guests}
                    onChange={(e) => setGuests(parseInt(e.target.value))}
                  >
                    {Array.from({ length: selectedCamp.maxGuests }, (_, i) => i + 1).map((num) => (
                      <option key={num} value={num}>
                        {num} {num === 1 ? 'Guest' : 'Guests'}
                      </option>
                    ))}
                  </select>
                </div>

                <h3 className="form-section-title">Your Details</h3>

                <div className="form-group">
                  <label htmlFor="guestName">Full Name *</label>
                  <input
                    type="text"
                    id="guestName"
                    value={guestName}
                    onChange={(e) => setGuestName(e.target.value)}
                    required
                    placeholder=""
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="guestEmail">Email *</label>
                    <input
                      type="email"
                      id="guestEmail"
                      value={guestEmail}
                      onChange={(e) => setGuestEmail(e.target.value)}
                      required
                      placeholder="example@gmail.com"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="guestPhone">Phone *</label>
                    <input
                      type="tel"
                      id="guestPhone"
                      value={guestPhone}
                      onChange={(e) => setGuestPhone(e.target.value)}
                      required
                      placeholder="+61 4XX XXX XXX"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="specialRequests">Your Message (Optional)</label>
                  <textarea
                    id="specialRequests"
                    value={specialRequests}
                    onChange={(e) => setSpecialRequests(e.target.value)}
                    rows={4}
                    placeholder="Tell us about your plans, the vessel you intend to travel with, any questions or special requests..."
                  />
                </div>

                <button
                  type="submit"
                  className="btn-submit"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Sending Enquiry...' : 'Send Enquiry'}
                </button>
              </form>
            </div>
            </div>
          )}
        </div>
      </div>

      {isGalleryOpen && assetImages.length > 0 && (
        <div
          className="asset-gallery-overlay"
          onClick={closeGallery}
          role="dialog"
          aria-modal="true"
          aria-label="Photo gallery"
        >
          <div className="asset-gallery" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="asset-gallery-close" onClick={closeGallery} aria-label="Close gallery">
              ×
            </button>

            <div
              className="asset-gallery-main"
              onTouchStart={onGalleryTouchStart}
              onTouchEnd={onGalleryTouchEnd}
              onTouchCancel={onGalleryTouchCancel}
            >
              <button type="button" className="asset-gallery-nav prev" onClick={showPrev} aria-label="Previous image">
                ‹
              </button>
              <img className="asset-gallery-image" src={assetImages[galleryIndex]} alt="" />
              <button type="button" className="asset-gallery-nav next" onClick={showNext} aria-label="Next image">
                ›
              </button>
            </div>

            <div className="asset-gallery-footer">
              <div className="asset-gallery-counter">
                {galleryIndex + 1} / {assetImages.length}
              </div>
              <div ref={thumbsRef} className="asset-gallery-thumbs" role="list">
                {visibleThumbs.map(({ src, idx, placeholder }) => (
                  <button
                    key={`${idx}-${placeholder ? 'ph' : 'img'}`}
                    type="button"
                    className={`asset-thumb ${idx === galleryIndex ? 'active' : ''} ${
                      placeholder ? 'placeholder' : ''
                    }`}
                    onClick={() => {
                      if (!placeholder && idx >= 0 && idx < assetImages.length) setGalleryIndex(idx);
                    }}
                    disabled={placeholder}
                    aria-label={`View image ${idx + 1}`}
                    role="listitem"
                  >
                    {src ? <img src={src} alt="" /> : <span aria-hidden="true" />}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookingPage;

